import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import { getLeadDuplicates, getContactDuplicates, mergeLeads, mergeContacts } from '../utils/crmApi';
import '../crm.css';

const strategyOptions = [
    { value: 'preferPrimary', label: 'Prefer Primary' },
    { value: 'preferLatest', label: 'Prefer Latest' },
    { value: 'combineNotes', label: 'Combine Notes' },
];

const DuplicatesPage = () => {
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();
    const [activeTab, setActiveTab] = useState('leads');
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selections, setSelections] = useState({});
    const [strategies, setStrategies] = useState({});

    const fetchGroups = async () => {
        setLoading(true);
        setError('');
        try {
            const data = activeTab === 'leads' ? await getLeadDuplicates() : await getContactDuplicates();
            setGroups(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || 'Failed to fetch duplicates');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, [activeTab]);

    const getGroupKey = (group) => `${group.keyType}:${group.keyValue}`;

    const initSelection = (group) => {
        const key = getGroupKey(group);
        if (selections[key]) return selections[key];

        const records = group.records || [];
        const ids = group.ids || records.map(r => r.id);

        const primaryId = ids[0];
        const mergeIds = ids.slice(1) || [];
        return { primaryId, mergeIds };
    };

    const handlePrimaryChange = (group, primaryId) => {
        const key = getGroupKey(group);
        const current = initSelection(group);
        const mergeIds = current.mergeIds.filter((id) => id !== primaryId);
        setSelections((prev) => ({ ...prev, [key]: { primaryId, mergeIds } }));
    };

    const handleMergeToggle = (group, recordId, checked) => {
        const key = getGroupKey(group);
        const current = initSelection(group);
        const mergeIds = new Set(current.mergeIds);
        if (checked) {
            mergeIds.add(recordId);
        } else {
            mergeIds.delete(recordId);
        }
        setSelections((prev) => ({ ...prev, [key]: { primaryId: current.primaryId, mergeIds: Array.from(mergeIds) } }));
    };

    const handleStrategyChange = (group, value) => {
        const key = getGroupKey(group);
        setStrategies((prev) => ({ ...prev, [key]: value }));
    };

    const handleMerge = async (group) => {
        const key = getGroupKey(group);
        const selection = initSelection(group);
        if (!selection.primaryId || selection.mergeIds.length === 0) {
            showError('Select a primary record and at least one record to merge');
            return;
        }

        const isConfirmed = await confirm({
            title: 'Merge Records',
            message: `Merge ${selection.mergeIds.length} record(s) into the primary record?`,
            confirmText: 'Merge',
            variant: 'danger',
        });
        if (!isConfirmed) return;

        try {
            const payload = {
                primaryId: selection.primaryId,
                duplicateIds: selection.mergeIds,
                strategy: strategies[key] || 'preferPrimary',
            };
            if (activeTab === 'leads') {
                await mergeLeads(payload);
            } else {
                await mergeContacts(payload);
            }
            showSuccess('Merge completed');
            fetchGroups();
        } catch (err) {
            showError(err.message || 'Merge failed');
        }
    };

    const renderGroup = (group) => {
        const key = getGroupKey(group);
        const selection = initSelection(group);
        const strategyValue = strategies[key] || 'preferPrimary';

        return (
            <div key={key} className="crm-card" style={{ marginBottom: '20px' }}>
                <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h3 className="crm-card-title">Duplicates by {group.keyType}</h3>
                        <div className="crm-card-subtitle">{group.keyValue}</div>
                    </div>
                    <div className="crm-merge-actions">
                        <select
                            className="crm-select crm-select-sm"
                            value={strategyValue}
                            onChange={(event) => handleStrategyChange(group, event.target.value)}
                        >
                            {strategyOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <button className="crm-btn crm-btn-danger crm-btn-sm" onClick={() => handleMerge(group)}>
                            Merge
                        </button>
                    </div>
                </div>
                <div className="crm-card-body" style={{ padding: 0 }}>
                    <table className="crm-table">
                        <thead>
                            <tr>
                                <th>Primary</th>
                                <th>Merge</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(group.records || []).map((record) => {
                                const name = record.name || `${record.firstName || ''} ${record.lastName || ''}`.trim();
                                const isPrimary = selection.primaryId === record.id;
                                const canMerge = record.id !== selection.primaryId;
                                return (
                                    <tr key={record.id}>
                                        <td>
                                            <input
                                                type="radio"
                                                name={`primary-${key}`}
                                                checked={isPrimary}
                                                onChange={() => handlePrimaryChange(group, record.id)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selection.mergeIds.includes(record.id)}
                                                onChange={(event) => handleMergeToggle(group, record.id, event.target.checked)}
                                                disabled={!canMerge}
                                            />
                                        </td>
                                        <td>{name || '-'}</td>
                                        <td>{record.email || '-'}</td>
                                        <td>{record.phone || record.mobile || '-'}</td>
                                        <td>{record.updatedAt ? new Date(record.updatedAt).toLocaleDateString() : '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="crm-container">
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Duplicates</h1>
                    <p className="crm-subtitle">Detect and merge duplicate records.</p>
                </div>
                <div className="crm-page-actions">
                    <button className="crm-btn crm-btn-secondary" onClick={fetchGroups}>Refresh</button>
                </div>
            </div>

            <div className="crm-tabs">
                <button
                    className={`crm-tab ${activeTab === 'leads' ? 'active' : ''}`}
                    onClick={() => setActiveTab('leads')}
                >
                    Leads
                </button>
                <button
                    className={`crm-tab ${activeTab === 'contacts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('contacts')}
                >
                    Contacts
                </button>
            </div>

            {loading ? (
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            ) : error ? (
                <div className="crm-empty-state">
                    <p>{error}</p>
                    <button className="crm-btn crm-btn-primary" onClick={fetchGroups}>Retry</button>
                </div>
            ) : groups.length === 0 ? (
                <div className="crm-empty-state">
                    <h3>No duplicates found</h3>
                    <p>Great! Your {activeTab} list is clean.</p>
                </div>
            ) : (
                groups.map(renderGroup)
            )}

            <ConfirmDialog />
        </div>
    );
};

export default DuplicatesPage;
